import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import Debts from '../components/Debts'
import PayoutDialog from '../components/PayoutDialog'
import { formatDate, formatHM, formatMoney, monthBounds, plural } from '../lib/format'
import { PAYOUT_STATUS_LABEL } from '../types'

export default function Payouts() {
  const { user, canSeeAll, can } = useAuth()
  const qc = useQueryClient()
  const bounds = monthBounds()

  // Админ смотрит расчёт по специалистам: на себя ставок и задач обычно нет,
  // и экран открывался пустым «нет задач».
  const [userId, setUserId] = useState<number>(user!.id)
  const [pickedUser, setPickedUser] = useState(false)
  const [from, setFrom] = useState(bounds.from)
  const [to, setTo] = useState(bounds.to)
  const [error, setError] = useState('')
  // Отмеченные задачи важнее фильтров: если галочки стоят, платим ровно за них
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [clientId, setClientId] = useState('')
  const [sourceId, setSourceId] = useState('')
  const [offset, setOffset] = useState('')
  const [openPayout, setOpenPayout] = useState<number | null>(null)

  // Фильтры готовых ведомостей живут отдельно от расчёта: расчёт всегда про один
  // период и одного специалиста, а в списке чаще ищут прошлые выплаты по всем.
  const [listUserId, setListUserId] = useState('')
  const [listFrom, setListFrom] = useState('')
  const [listTo, setListTo] = useState('')
  const [listStatus, setListStatus] = useState('')
  const listFiltered = Boolean(listUserId || listFrom || listTo || listStatus)
  const resetListFilters = () => {
    setListUserId('')
    setListFrom('')
    setListTo('')
    setListStatus('')
  }

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users(), enabled: canSeeAll })

  useEffect(() => {
    if (pickedUser || !canSeeAll || !users.data?.length) return
    const other = users.data.find((u) => u.id !== user!.id)
    if (other) setUserId(other.id)
  }, [users.data, canSeeAll, pickedUser, user])
  const payouts = useQuery({
    queryKey: ['payouts', listUserId, listFrom, listTo, listStatus],
    queryFn: () =>
      api.payouts({
        user_id: listUserId ? Number(listUserId) : undefined,
        date_from: listFrom || undefined,
        date_to: listTo || undefined,
        payout_status: listStatus || undefined,
      }),
  })

  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients() })
  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources() })

  const preview = useQuery({
    queryKey: ['payout-preview', userId, from, to, clientId, sourceId],
    queryFn: () =>
      api.payoutPreview(userId, from, to, {
        client_id: clientId ? Number(clientId) : undefined,
        source_id: sourceId ? Number(sourceId) : undefined,
      }),
  })

  // Список задач сменился — снимаем галочки с исчезнувших
  useEffect(() => {
    const ids = new Set((preview.data?.rows ?? []).map((r) => r.task_id))
    setPicked((prev) => new Set([...prev].filter((id) => ids.has(id))))
  }, [preview.data])

  const rows = preview.data?.rows ?? []
  const chosen = rows.filter((r) => picked.has(r.task_id))
  const effective = chosen.length > 0 ? chosen : rows
  const accrued = effective.reduce((sum, r) => sum + Number(r.amount), 0)
  const chosenHours = effective.reduce((sum, r) => sum + Number(r.hours), 0)
  const debt = Number(preview.data?.debt_balance ?? 0)
  const offsetValue = Math.max(0, Number(offset) || 0)
  // Работой гасится только долг сотрудника перед компанией — больше него зачитывать нечего
  const offsetCap = Math.min(Math.max(0, debt), accrued)
  const cols = canSeeAll ? 5 : 4

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['payouts'] })
    qc.invalidateQueries({ queryKey: ['payout-preview'] })
    qc.invalidateQueries({ queryKey: ['tasks'] })
    qc.invalidateQueries({ queryKey: ['kpi'] })
    qc.invalidateQueries({ queryKey: ['debt-balances'] })
    qc.invalidateQueries({ queryKey: ['debts'] })
  }

  const create = useMutation({
    mutationFn: () =>
      api.createPayout({
        user_id: userId,
        period_start: from,
        period_end: to,
        client_id: clientId ? Number(clientId) : null,
        source_id: sourceId ? Number(sourceId) : null,
        task_ids: chosen.length > 0 ? chosen.map((r) => r.task_id) : null,
        debt_offset: offsetValue,
      }),
    onSuccess: () => {
      setPicked(new Set())
      setOffset('')
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось сформировать ведомость'),
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'draft' | 'approved' | 'paid' }) =>
      api.setPayoutStatus(id, status),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не удалось изменить статус'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deletePayout(id),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не удалось удалить ведомость'),
  })

  return (
    <>
      <h1 className="page-title">Выплаты специалистам</h1>

      {error && (
        <div className="error" onClick={() => setError('')}>
          {error} (нажмите, чтобы скрыть)
        </div>
      )}

      <div className="payout-columns">
        <div className="payout-pane">
          <div className="pane-head">
            <h2>Расчёт за период</h2>
          </div>
          <div className="toolbar filters-calc">
            {canSeeAll && (
              <div className="field">
                <label>Специалист</label>
                <select
                  value={userId}
                  onChange={(e) => {
                    setPickedUser(true)
                    setUserId(Number(e.target.value))
                  }}
                >
                  {users.data?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label>с</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="field">
              <label>по</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="field">
              <label>Клиент</label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Все</option>
                {clients.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Источник</label>
              <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                <option value="">Все</option>
                {sources.data?.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pane-body">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {canSeeAll && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        title="Отметить все"
                        checked={rows.length > 0 && picked.size === rows.length}
                        onChange={(e) =>
                          setPicked(e.target.checked ? new Set(rows.map((r) => r.task_id)) : new Set())
                        }
                      />
                    </th>
                  )}
                  <th>Дата</th>
                  <th>Задача</th>
                  <th className="num">Часы</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {preview.isLoading && (
                  <tr>
                    <td colSpan={cols} className="empty">
                      Считаем…
                    </td>
                  </tr>
                )}
                {rows.length === 0 && !preview.isLoading && (
                  <tr>
                    <td colSpan={cols} className="empty">
                      За период нет завершённых задач со статусом «К выплате»
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.task_id} className={picked.has(r.task_id) ? 'running' : undefined}>
                    {canSeeAll && (
                      <td>
                        <input
                          type="checkbox"
                          checked={picked.has(r.task_id)}
                          onChange={(e) => {
                            const next = new Set(picked)
                            if (e.target.checked) next.add(r.task_id)
                            else next.delete(r.task_id)
                            setPicked(next)
                          }}
                        />
                      </td>
                    )}
                    <td className="mono nowrap">{formatDate(r.work_date)}</td>
                    <td className="cell-task">
                      {r.title}
                      <span className="cell-sub">
                        <span className="client-name">{r.client_name}</span>
                        {r.source_name ? ` · ${r.source_name}` : ''}
                      </span>
                    </td>
                    <td className="num nowrap">
                      <span className={r.by_estimate ? 'estimate-value' : 'mono'}>
                        {Number(r.hours)} ч
                      </span>
                      <span className="cell-sub" title="Фактически отработано">
                        факт {formatHM(r.seconds)}
                      </span>
                    </td>
                    <td className="num mono nowrap">
                      {formatMoney(r.amount)}
                      <span className="cell-sub">{formatMoney(r.rate)}/ч</span>
                    </td>
                  </tr>
                ))}
                {rows.length > 0 && (
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={canSeeAll ? 3 : 2}>
                      ИТОГО{chosen.length > 0 ? ` по отмеченным (${chosen.length})` : ''}
                    </td>
                    <td className="num mono">{chosenHours} ч</td>
                    <td className="num mono">{formatMoney(accrued)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p style={{ color: 'var(--muted)' }}>
            В расчёт попадают завершённые задачи со статусом оплаты «К выплате». Часы берутся
            из оценки задачи, а если оценки нет — из фактически отработанного времени. Ставка —
            из справочника на дату задачи: по проекту → по клиенту → базовая.
          </p>

          {canSeeAll && (
            <div className="payout-settle">
              {/* Баланс показываем всегда, даже когда платить не за что: иначе непонятно,
                  есть ли вообще долг и что с ним делать. */}
              <div className={`debt-state ${debt > 0 ? 'owes' : debt < 0 ? 'owed' : 'clear'}`}>
                <div className="debt-state-head">
                  <span className="payout-summary-label">
                    {debt > 0
                      ? 'Сотрудник должен компании'
                      : debt < 0
                        ? 'Компания должна сотруднику'
                        : 'Взаиморасчёты'}
                  </span>
                  <span className="payout-summary-value mono">
                    {debt === 0 ? 'долгов нет' : formatMoney(Math.abs(debt))}
                  </span>
                </div>
                {debt > 0 && (
                  <p className="debt-state-hint">
                    Долг можно закрыть работой: впишите сумму в «Зачесть работой» — она
                    уменьшит выплату на руки и на столько же спишет долг. Операция попадёт
                    во «Взаиморасчёты» вместе с ведомостью.
                  </p>
                )}
                {debt < 0 && (
                  <p className="debt-state-hint">
                    Такой долг работой не гасится — сотрудник и так получит начисленное.
                    Возврат проводится деньгами: операция «Вернули ему» во «Взаиморасчётах»
                    внизу страницы.
                  </p>
                )}
              </div>

              {rows.length > 0 && (
                <>
                  <div className="payout-actions-info">
                    <div>
                      <span className="payout-summary-label">К начислению</span>
                      <span className="payout-summary-value mono">{formatMoney(accrued)}</span>
                    </div>
                    <div>
                      <span className="payout-summary-label">Зачёт долга</span>
                      <span className="payout-summary-value mono">
                        {offsetValue > 0 ? `− ${formatMoney(offsetValue)}` : '—'}
                      </span>
                    </div>
                    <div className="payout-summary-total">
                      <span className="payout-summary-label">На руки</span>
                      <span className="payout-summary-value mono">
                        {formatMoney(Math.max(0, accrued - offsetValue))}
                      </span>
                    </div>
                  </div>

                  <div className="payout-offset">
                    <div className="field">
                      <label>Зачесть работой, ₽</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        max={accrued}
                        style={{ width: 140 }}
                        value={offset}
                        placeholder="0"
                        disabled={offsetCap <= 0}
                        title={
                          offsetCap <= 0 ? 'Зачитывать нечего: долга перед компанией нет' : undefined
                        }
                        onChange={(e) => setOffset(e.target.value)}
                      />
                    </div>
                    {offsetCap > 0 && (
                      <button
                        type="button"
                        className="btn small"
                        onClick={() => setOffset(String(offsetCap))}
                      >
                        Зачесть {formatMoney(offsetCap)}
                      </button>
                    )}
                    {offsetValue > 0 && (
                      <button type="button" className="link-inline" onClick={() => setOffset('')}>
                        убрать зачёт
                      </button>
                    )}
                  </div>

                  {offsetValue > accrued && (
                    <div className="error">Зачёт больше начисленного — уменьшите сумму</div>
                  )}
                  {debt > 0 && offsetValue > debt && offsetValue <= accrued && (
                    <div className="warn-inline">
                      Зачёт больше долга на {formatMoney(offsetValue - debt)} — после ведомости
                      компания останется должна сотруднику эту сумму
                    </div>
                  )}

                  <button
                    className="btn primary"
                    disabled={create.isPending || offsetValue > accrued}
                    onClick={() => create.mutate()}
                  >
                    {chosen.length > 0
                      ? `Сформировать по ${chosen.length} задачам`
                      : 'Сформировать по всем задачам'}
                  </button>
                </>
              )}
            </div>
          )}
          </div>
        </div>

        <div className="payout-pane">
          <div className="pane-head">
            <h2>Ведомости</h2>
            {/* Место под кнопку занято всегда: появляясь, она иначе двигала бы
                панели фильтров в обеих половинах. */}
            <button
              className="btn small pane-head-action"
              hidden={!canSeeAll}
              style={listFiltered ? undefined : { visibility: 'hidden' }}
              tabIndex={listFiltered ? undefined : -1}
              onClick={resetListFilters}
            >
              Сбросить фильтры
            </button>
          </div>
          <div className="toolbar filters-list">
            {canSeeAll && (
              <div className="field">
                <label>Специалист</label>
                <select value={listUserId} onChange={(e) => setListUserId(e.target.value)}>
                  <option value="">Все</option>
                  {users.data?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label>с</label>
              <input
                type="date"
                value={listFrom}
                onChange={(e) => setListFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label>по</label>
              <input type="date" value={listTo} onChange={(e) => setListTo(e.target.value)} />
            </div>
            <div className="field">
              <label>Статус</label>
              <select value={listStatus} onChange={(e) => setListStatus(e.target.value)}>
                <option value="">Все</option>
                {Object.entries(PAYOUT_STATUS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pane-body">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Период</th>
                  <th className="num">На руки</th>
                  <th>Статус</th>
                  {canSeeAll && <th />}
                </tr>
              </thead>
              <tbody>
                {payouts.data?.length === 0 && (
                  <tr>
                    <td colSpan={canSeeAll ? 4 : 3} className="empty">
                      {listFiltered ? 'Под фильтры ничего не подходит' : 'Ведомостей ещё нет'}
                    </td>
                  </tr>
                )}
                {payouts.data?.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-payout">
                      <button className="link-inline mono" onClick={() => setOpenPayout(p.id)}>
                        {formatDate(p.period_start)} — {formatDate(p.period_end)}
                      </button>
                      <span className="cell-sub">
                        {canSeeAll ? `${p.user_name} · ` : ''}
                        {p.items.length}{' '}
                        {plural(p.items.length, 'задача', 'задачи', 'задач')} ·{' '}
                        {formatHM(p.total_seconds)}
                      </span>
                    </td>
                    <td className="num mono cell-payout-sum">
                      <b>{formatMoney(p.paid_amount)}</b>
                      <span className="cell-sub">
                        начислено {formatMoney(p.total_amount)}
                        {Number(p.debt_offset) > 0 && ` · зачёт ${formatMoney(p.debt_offset)}`}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${p.status}`}>{PAYOUT_STATUS_LABEL[p.status]}</span>
                    </td>
                    {canSeeAll && (
                      <td>
                        {p.status !== 'paid' && (
                          <button
                            className="btn small"
                            onClick={() => setStatus.mutate({ id: p.id, status: 'paid' })}
                          >
                            Выплатить
                          </button>
                        )}
                        {p.status === 'paid' && (
                          <button
                            className="btn small"
                            onClick={() => setStatus.mutate({ id: p.id, status: 'approved' })}
                          >
                            Отменить
                          </button>
                        )}{' '}
                        {can('payouts.manage') && p.status !== 'paid' && (
                          <button
                            className="btn small danger"
                            onClick={() => {
                              if (confirm('Удалить ведомость? Задачи вернутся в статус «К выплате».'))
                                remove.mutate(p.id)
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      </div>

      {can('debts.view') && <Debts />}


      {openPayout !== null && (
        <PayoutDialog payoutId={openPayout} onClose={() => setOpenPayout(null)} />
      )}
    </>
  )
}
